import {
  Box,
  Button as MuiButton,
  ClickAwayListener,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popper,
  Typography,
  SelectChangeEvent,
  Switch,
} from '@mui/material'
import { intl } from '../../../../../i18n'
import { colorPalette } from '../../../../style/colorPalette'
import {
  ChangeEvent,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ArrowBackIos,
  ArrowForwardIos,
  ExpandLess,
  ExpandMore,
  Flag as FlagIcon,
  NavigateNextRounded as NavigateNextRoundedIcon,
  Route as RouteIcon,
  ViewTimeline,
} from '@mui/icons-material'
import { connect } from 'react-redux'
import store, { AllState } from '../../../../../store'
import {
  DisplayRange,
  GanttDisplayOptions,
  GanttDisplayUnit,
  GanttParameterVO,
} from '../../../../../domain/value-object/GanttParameterVO'
import { changeGanttParameter } from '../../../../../store/project'
import { addGlobalMessage, MessageLevel } from '../../../../../store/messages'
import {
  DayOfWeek,
  mapDayOfWeekToNumber,
} from '../../../../../lib/functions/report'
import DateSelect, {
  DateSelectType,
} from '../../../../components/toolbars/common/DateSelect'
import DateVO from '../../../../../vo/DateVO'
import { LegacySwitch } from '../../../../components/inputs/Switch'
import { Button } from '../../../../components/buttons'
import { Checkbox } from '../../../../components/inputs/Checkbox'
import Select from '../../../../components/editors/select/Select'
import ExclusiveToggleGroup from '../../../../components/editors/toggle/ExclusiveToggleGroup'
import _ from 'lodash'
import { featureFlagService } from '../../../../../utils/feature-flag'
import { styled } from '@mui/system'
import { Text } from '../../../../components/typography'

const isGanttV2 = featureFlagService.isEnabled('gantt-v2')

const RANGE_PRESET_FIT_TO_ROOT = 'FIT_TO_ROOT' as const
const RANGE_PRESET_DATE_SPECIFIED = 'DATE_SPECIFIED' as const
type RangePreset =
  | typeof RANGE_PRESET_FIT_TO_ROOT
  | typeof RANGE_PRESET_DATE_SPECIFIED

const toDateSelectType = (unit: GanttDisplayUnit): DateSelectType => {
  switch (unit) {
    case GanttDisplayUnit.QUARTER:
      return DateSelectType.QUARTER
    case GanttDisplayUnit.MONTH:
      return DateSelectType.YEAR_MONTH
    case GanttDisplayUnit.WEEK:
      return DateSelectType.WEEK
    default:
      return DateSelectType.DATE
  }
}

// ---------- V2 styled components ----------

const V2SectionColumn = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '16px',
})

const V2SectionHeader = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '5px',
})

const V2PresetSelectContainer = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  background: colorPalette.monotone[0],
  display: 'flex',
  alignItems: 'center',
  width: '160px',
  height: '30px',
  '& .MuiSelect-select': {
    padding: '4px 10px !important',
  },
  '& .MuiSelect-icon': {
    right: '5px',
    height: '13px',
    width: '13px',
    color: colorPalette.monotone[4],
  },
})

const V2DateRangeRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
})

const V2DateSeparator = styled(Text)({
  flexShrink: 0,
})

const V2ShiftButton = styled(IconButton)({
  borderRadius: '50%',
  width: '24px',
  height: '24px',
  padding: 0,
})

const V2OptionsGrid = styled(FormGroup)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '4px 16px',
})

const ProgressLineContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '16px',
})

const GanttToggleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  paddingBottom: '16px',
  borderBottom: `1px solid ${colorPalette.monotone[1]}`,
})

const ActionButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 12px',
  minWidth: 0,
  flex: 1,
})

const SettingTriggerButton = styled('button')({
  background: 'transparent',
  border: 0,
  fontSize: 12,
  fontWeight: 500,
  display: 'flex',
  flexDirection: 'row',
  padding: '5px 10px',
  alignItems: 'center',
  cursor: 'pointer',
})

type Props = {
  onSwitchingGanttChart: (value: boolean) => void
  value: boolean
  parameter: GanttParameterVO | undefined
  projectUuid?: string
  rootScheduledStartDate?: string
  rootScheduledEndDate?: string
  projectScheduledStartDate?: string
  projectScheduledEndDate?: string
  isMilestoneEnabled?: boolean
  onOpenMilestoneDialog?: () => void
  isDependencyCriticalPathEnabled?: boolean
  onOpenCriticalPathDialog?: () => void
}
const GanttChartParameter = ({
  onSwitchingGanttChart,
  value,
  parameter,
  projectUuid,
  rootScheduledStartDate,
  rootScheduledEndDate,
  projectScheduledStartDate,
  projectScheduledEndDate,
  isMilestoneEnabled,
  onOpenMilestoneDialog,
  isDependencyCriticalPathEnabled,
  onOpenCriticalPathDialog,
}: Props) => {
  const [openGanttChartSettingPopper, setOpenGanttChartSettingPopper] =
    useState<boolean>(false)
  const [settingAnchorEl, setSettingAnchorEl] = useState<Element>()
  const [openUnitPopper, setOpenUnitPopper] = useState<boolean>(false)
  const [unitAnchorEl, setUnitAnchorEl] = useState<Element>()
  const [openStartDayPopper, setOpenStartDayPopper] = useState<boolean>(false)
  const [startDayAnchorEl, setStartDayAnchorEl] = useState<Element>()
  const [rangePreset, setRangePreset] = useState<RangePreset>(
    RANGE_PRESET_DATE_SPECIFIED
  )
  const [isPresetSelectOpen, setIsPresetSelectOpen] = useState(false)

  const ganttParameter = parameter ?? GanttParameterVO.defaultValue()
  const [unit, setUnit] = useState<GanttDisplayUnit>(ganttParameter.unit)
  const [startDay, setStartDay] = useState<DayOfWeek>(ganttParameter.startDay)
  const [range, setRange] = useState<DisplayRange>(ganttParameter.range)
  const [displayOptions, setDisplayOptions] = useState<GanttDisplayOptions>(
    ganttParameter.displayOptions
  )

  const openMenuIconRef = useRef<HTMLSpanElement>(null)

  const dispatchParameter = useCallback(
    (
      newUnit: GanttDisplayUnit,
      newStartDay: DayOfWeek,
      newRange: DisplayRange,
      newDisplayOptions: GanttDisplayOptions
    ) => {
      if (!projectUuid) return
      store.dispatch(
        changeGanttParameter(
          projectUuid,
          new GanttParameterVO(
            newUnit,
            newStartDay,
            newRange,
            newDisplayOptions
          )
        )
      )
    },
    [projectUuid]
  )

  const debouncedDispatchRange = useMemo(
    () =>
      _.debounce((newRange: DisplayRange) => {
        dispatchParameter(unit, startDay, newRange, displayOptions)
      }, 300),
    [unit, startDay, displayOptions, dispatchParameter]
  )

  useEffect(() => {
    return () => debouncedDispatchRange.cancel()
  }, [debouncedDispatchRange])

  const isAtMaxRange = useMemo(
    () => GanttParameterVO.isAtMaxRange(range),
    [range]
  )

  const dateSelectType = toDateSelectType(unit)
  const calendarStartDay =
    unit === GanttDisplayUnit.WEEK
      ? mapDayOfWeekToNumber(startDay) % 7
      : undefined

  const shiftBound = useCallback(
    (bound: 'start' | 'end', direction: 1 | -1) => {
      const isStart = bound === 'start'
      const baseDate = isStart ? range.start : range.end
      const round = isStart
        ? GanttParameterVO.roundStartDate
        : GanttParameterVO.roundEndDate
      const newDate = round(
        GanttParameterVO.incrementDate(baseDate, unit, direction),
        unit,
        startDay
      )
      const newRange = isStart
        ? { start: newDate, end: range.end }
        : { start: range.start, end: newDate }
      if (isStart && newDate.isAfter(range.end)) return
      if (!isStart && newDate.isBefore(range.start)) return
      const clamped = GanttParameterVO.clampRange(
        newRange.start,
        newRange.end,
        bound
      )
      setRange(clamped)
      setRangePreset(RANGE_PRESET_DATE_SPECIFIED)
      dispatchParameter(unit, startDay, clamped, displayOptions)
    },
    [range, unit, startDay, displayOptions, dispatchParameter]
  )

  const handleDateChange = useCallback(
    (bound: 'start' | 'end', v?: string) => {
      if (!v) return
      const isStart = bound === 'start'
      const round = isStart
        ? GanttParameterVO.roundStartDate
        : GanttParameterVO.roundEndDate
      const rounded = round(new DateVO(v), unit, startDay)
      const other = isStart
        ? range.end.isBefore(rounded)
          ? GanttParameterVO.roundEndDate(rounded, unit, startDay)
          : range.end
        : range.start.isAfter(rounded)
          ? GanttParameterVO.roundStartDate(rounded, unit, startDay)
          : range.start
      const newRange = isStart
        ? { start: rounded, end: other }
        : { start: other, end: rounded }
      const clamped = GanttParameterVO.clampRange(
        newRange.start,
        newRange.end,
        bound
      )
      setRange(clamped)
      setRangePreset(RANGE_PRESET_DATE_SPECIFIED)
      debouncedDispatchRange(clamped)
    },
    [range, unit, startDay, debouncedDispatchRange]
  )

  useEffect(() => {
    if (parameter) {
      setUnit(parameter.unit)
      setStartDay(parameter.startDay)
      setRange(parameter.range)
      setDisplayOptions(parameter.displayOptions)
    }
  }, [parameter])

  const handleGanttChartSettingClose = () => {
    if (isPresetSelectOpen) return
    if (isGanttV2) {
      debouncedDispatchRange.flush()
    }
    setSettingAnchorEl(undefined)
    setOpenGanttChartSettingPopper(false)
    setOpenUnitPopper(false)
    setOpenStartDayPopper(false)
  }

  const handleGanttChartSettingApply = () => {
    if (openGanttChartSettingPopper) {
      store.dispatch(
        changeGanttParameter(
          projectUuid!,
          new GanttParameterVO(unit, startDay, range, displayOptions)
        )
      )
    }
    setSettingAnchorEl(undefined)
    setOpenGanttChartSettingPopper(false)
    setOpenUnitPopper(false)
    setOpenStartDayPopper(false)
  }

  const handleUnitClose = () => {
    setUnitAnchorEl(undefined)
    setOpenUnitPopper(false)
  }

  const handleStartDayClose = () => {
    setStartDayAnchorEl(undefined)
    setOpenStartDayPopper(false)
  }

  const handleUnitChange = useCallback(
    (newUnit: string) => {
      const typedUnit = newUnit as GanttDisplayUnit
      setUnit(typedUnit)
      if (isGanttV2) {
        const newStart = GanttParameterVO.roundStartDate(
          range.start,
          typedUnit,
          startDay
        )
        const newEnd = GanttParameterVO.roundEndDate(
          range.end,
          typedUnit,
          startDay
        )
        const newRange = { start: newStart, end: newEnd }
        setRange(newRange)
        dispatchParameter(typedUnit, startDay, newRange, displayOptions)
      }
    },
    [range, startDay, displayOptions, dispatchParameter]
  )

  const handleStartDayChange = useCallback(
    (newStartDay: string) => {
      const typedDay = newStartDay as DayOfWeek
      setStartDay(typedDay)
      if (isGanttV2) {
        dispatchParameter(unit, typedDay, range, displayOptions)
      }
    },
    [unit, range, displayOptions, dispatchParameter]
  )

  const unitStartDayContent = (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Text
          variant="2sMedium"
          color={colorPalette.monotone[5]}
          style={{ marginBottom: '5px' }}
        >
          {intl.formatMessage({ id: 'wbs.header.gantt.unit' })}
        </Text>
        <ExclusiveToggleGroup
          value={unit}
          onChange={handleUnitChange}
          disabled={!value}
          options={Object.values(GanttDisplayUnit)
            .filter(v => isGanttV2 || v !== GanttDisplayUnit.QUARTER)
            .map(v => ({
              value: v,
              label: intl.formatMessage({
                id: `gantt.displayUnit.${v.toLowerCase()}`,
              }),
            }))}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Text
          variant="2sMedium"
          color={colorPalette.monotone[5]}
          style={{ marginBottom: '5px' }}
        >
          {intl.formatMessage({ id: 'wbs.header.gantt.startDay' })}
        </Text>
        <ExclusiveToggleGroup
          value={startDay}
          onChange={handleStartDayChange}
          disabled={unit !== GanttDisplayUnit.WEEK || !value}
          options={Object.values(DayOfWeek).map(v => ({
            value: v,
            label: intl.formatMessage({
              id: `dayOfWeek.short.${v.toLowerCase()}`,
            }),
          }))}
        />
      </div>
    </div>
  )

  if (isGanttV2) {
    return (
      <div style={{ minWidth: 'fit-content' }}>
        <ClickAwayListener onClickAway={handleGanttChartSettingClose}>
          <div>
            <SettingTriggerButton
              onClick={event => {
                if (openGanttChartSettingPopper) {
                  setSettingAnchorEl(undefined)
                  setOpenGanttChartSettingPopper(false)
                } else {
                  setSettingAnchorEl(event.currentTarget)
                  setOpenGanttChartSettingPopper(true)
                }
              }}
            >
              <ViewTimeline
                sx={{
                  fontSize: 18,
                  color: colorPalette.monotone[4],
                }}
              />
              <Text
                variant="2sMedium"
                style={{
                  color: colorPalette.monotone[5],
                  marginLeft: '4px',
                }}
              >
                {intl.formatMessage({ id: 'wbs.header.gantt.setting' })}
              </Text>
              <NavigateNextRoundedIcon
                sx={{
                  marginLeft: '5px',
                  color: colorPalette.monotone[4],
                  cursor: 'pointer',
                  transform: openGanttChartSettingPopper
                    ? 'rotate(90deg)'
                    : 'rotate(0deg)',
                }}
              />
            </SettingTriggerButton>
            <Popper
              open={!!settingAnchorEl}
              anchorEl={settingAnchorEl}
              sx={{
                width: 'fit-content',
                height: 'fit-content',
                background: colorPalette.monotone[0],
                borderRadius: '4px',
                boxShadow: '0px 1px 2px #0000004D',
                padding: '20px',
                zIndex: 1300,
              }}
              placement={'bottom'}
              modifiers={[{ name: 'offset', options: { offset: [0, 5] } }]}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <GanttToggleRow>
                  <Text variant="2sMedium" color={colorPalette.monotone[5]}>
                    {intl.formatMessage({
                      id: 'wbs.header.gantt.title',
                    })}
                  </Text>
                  <Switch
                    size="small"
                    checked={value}
                    onChange={() => onSwitchingGanttChart(!value)}
                  />
                </GanttToggleRow>
                {unitStartDayContent}
                <V2SectionColumn>
                  <V2SectionHeader>
                    <Text variant="2sMedium" color={colorPalette.monotone[5]}>
                      {intl.formatMessage({
                        id: 'wbs.header.gantt.displayRange',
                      })}
                    </Text>
                    <V2PresetSelectContainer>
                      <Select
                        fullWidth={true}
                        value={rangePreset}
                        IconComponent={ExpandMore}
                        open={isPresetSelectOpen}
                        onOpen={() => setIsPresetSelectOpen(true)}
                        onClose={() => setIsPresetSelectOpen(false)}
                        MenuProps={{
                          sx: { zIndex: 1400 },
                        }}
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          const newPreset = e.target.value as RangePreset
                          if (newPreset === RANGE_PRESET_FIT_TO_ROOT) {
                            const fitStartDate =
                              rootScheduledStartDate ??
                              projectScheduledStartDate
                            const fitEndDate =
                              rootScheduledEndDate ?? projectScheduledEndDate
                            if (!fitStartDate || !fitEndDate) return
                            const newStart = GanttParameterVO.roundStartDate(
                              new DateVO(fitStartDate),
                              unit,
                              startDay
                            )
                            const newEnd = GanttParameterVO.roundEndDate(
                              new DateVO(fitEndDate),
                              unit,
                              startDay
                            )
                            const clamped = GanttParameterVO.clampRange(
                              newStart,
                              newEnd,
                              'start'
                            )
                            const wasTruncated = !clamped.end.isSame(newEnd)
                            if (wasTruncated) {
                              store.dispatch(
                                addGlobalMessage({
                                  type: MessageLevel.WARN,
                                  title: intl.formatMessage({
                                    id: 'gantt.fitToRootPeriod.truncated',
                                  }),
                                })
                              )
                            }
                            setRange(clamped)
                            dispatchParameter(
                              unit,
                              startDay,
                              clamped,
                              displayOptions
                            )
                          }
                          setRangePreset(newPreset)
                        }}
                        disableUnderline={true}
                        options={[
                          {
                            value: RANGE_PRESET_FIT_TO_ROOT,
                            label: intl.formatMessage({
                              id: 'gantt.fitToRootPeriod',
                            }),
                            disabled:
                              !(
                                rootScheduledStartDate ??
                                projectScheduledStartDate
                              ) ||
                              !(
                                rootScheduledEndDate ?? projectScheduledEndDate
                              ),
                          },
                          {
                            value: RANGE_PRESET_DATE_SPECIFIED,
                            label: intl.formatMessage({
                              id: 'dateSpecified',
                            }),
                          },
                        ]}
                        sx={{
                          fontSize: '12px',
                          color: colorPalette.monotone[4],
                        }}
                        disabled={!value}
                      />
                    </V2PresetSelectContainer>
                  </V2SectionHeader>
                  <V2DateRangeRow>
                    <V2ShiftButton
                      onClick={() => shiftBound('start', -1)}
                      disabled={!value || isAtMaxRange}
                      size="small"
                    >
                      <ArrowBackIos sx={{ fontSize: '12px' }} />
                    </V2ShiftButton>
                    <DateSelect
                      type={dateSelectType}
                      value={range.start}
                      onChange={v => handleDateChange('start', v)}
                      disabled={!value}
                      sx={{ width: '120px' }}
                      calendarStartDay={calendarStartDay}
                    />
                    <V2ShiftButton
                      onClick={() => shiftBound('start', 1)}
                      disabled={!value}
                      size="small"
                    >
                      <ArrowForwardIos sx={{ fontSize: '12px' }} />
                    </V2ShiftButton>
                    <V2DateSeparator
                      variant="2s"
                      color={colorPalette.monotone[3]}
                    >
                      ～
                    </V2DateSeparator>
                    <V2ShiftButton
                      onClick={() => shiftBound('end', -1)}
                      disabled={!value}
                      size="small"
                    >
                      <ArrowBackIos sx={{ fontSize: '12px' }} />
                    </V2ShiftButton>
                    <DateSelect
                      type={dateSelectType}
                      value={range.end}
                      onChange={v => handleDateChange('end', v)}
                      disabled={!value}
                      sx={{ width: '120px' }}
                      calendarStartDay={calendarStartDay}
                    />
                    <V2ShiftButton
                      onClick={() => shiftBound('end', 1)}
                      disabled={!value || isAtMaxRange}
                      size="small"
                    >
                      <ArrowForwardIos sx={{ fontSize: '12px' }} />
                    </V2ShiftButton>
                  </V2DateRangeRow>
                </V2SectionColumn>
                <V2SectionColumn>
                  <Text
                    variant="2sMedium"
                    color={colorPalette.monotone[5]}
                    style={{ marginBottom: '5px' }}
                  >
                    {intl.formatMessage({
                      id: 'gantt.displayOption.title',
                    })}
                  </Text>
                  <V2OptionsGrid row={true}>
                    {(
                      [
                        'showTypeIcon',
                        'showStatus',
                        'showName',
                        'showAssignee',
                        'showProgressRate',
                      ] as (keyof GanttDisplayOptions)[]
                    ).map(key => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            size="s"
                            checked={displayOptions[key]}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const newOptions = {
                                ...displayOptions,
                                [key]: e.target.checked,
                              }
                              setDisplayOptions(newOptions)
                              dispatchParameter(
                                unit,
                                startDay,
                                range,
                                newOptions
                              )
                            }}
                          />
                        }
                        label={intl.formatMessage({
                          id: `gantt.displayOption.${key}`,
                        })}
                        sx={{
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            color: colorPalette.monotone[5],
                            fontSize: '12px',
                          },
                        }}
                        disabled={!value}
                      />
                    ))}
                  </V2OptionsGrid>
                </V2SectionColumn>
                <ProgressLineContainer>
                  <Text variant="2s" color={colorPalette.monotone[5]}>
                    {intl.formatMessage({
                      id: 'gantt.progressLine',
                    })}
                  </Text>
                  <Switch
                    size="small"
                    checked={displayOptions.showProgressLine}
                    onChange={() => {
                      const newOptions = {
                        ...displayOptions,
                        showProgressLine: !displayOptions.showProgressLine,
                      }
                      setDisplayOptions(newOptions)
                      dispatchParameter(unit, startDay, range, newOptions)
                    }}
                    disabled={!value}
                  />
                </ProgressLineContainer>
                {(isMilestoneEnabled || isDependencyCriticalPathEnabled) && (
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: `1px solid ${colorPalette.monotone[1]}`,
                    }}
                  >
                    {isMilestoneEnabled && (
                      <ActionButton
                        colorPattern="skyBlue"
                        variant="outlined"
                        onClick={() => {
                          handleGanttChartSettingClose()
                          onOpenMilestoneDialog?.()
                        }}
                      >
                        <FlagIcon sx={{ fontSize: 14 }} />
                        <Text variant="2s" color="inherit">
                          {intl.formatMessage({
                            id: 'milestone.dialog.title',
                          })}
                        </Text>
                      </ActionButton>
                    )}
                    {isDependencyCriticalPathEnabled && (
                      <ActionButton
                        colorPattern="pink"
                        variant="outlined"
                        onClick={() => {
                          handleGanttChartSettingClose()
                          onOpenCriticalPathDialog?.()
                        }}
                      >
                        <RouteIcon sx={{ fontSize: 14 }} />
                        <Text variant="2s" color="inherit">
                          {intl.formatMessage({
                            id: 'wbsItemLink.criticalPath.dialog.title',
                          })}
                        </Text>
                      </ActionButton>
                    )}
                  </div>
                )}
              </div>
            </Popper>
          </div>
        </ClickAwayListener>
      </div>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: '10px',
        minWidth: 'fit-content',
      }}
    >
      <Typography sx={{ color: colorPalette.monotone[5] }}>
        {intl.formatMessage({ id: 'wbs.header.gantt.title' })}
      </Typography>
      <FormControlLabel
        sx={{ marginRight: 0 }}
        control={<LegacySwitch checked={value} />}
        label={undefined}
        checked={value}
        onChange={() => {
          onSwitchingGanttChart(value ? false : true)
        }}
      />
      <ClickAwayListener onClickAway={handleGanttChartSettingClose}>
        <div>
          <span ref={openMenuIconRef}>
            {openGanttChartSettingPopper ? (
              <ExpandLess
                sx={{
                  margin: '0 5px 0 auto',
                  height: '1em',
                  width: '1em',
                  color: colorPalette.monotone[4],
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSettingAnchorEl(undefined)
                  setOpenGanttChartSettingPopper(false)
                }}
              />
            ) : (
              <ExpandMore
                sx={{
                  margin: '0 5px 0 auto',
                  height: '1em',
                  width: '1em',
                  color: colorPalette.monotone[4],
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSettingAnchorEl(openMenuIconRef.current || undefined)
                  setOpenGanttChartSettingPopper(true)
                }}
              />
            )}
          </span>
          <Popper
            open={!!settingAnchorEl}
            anchorEl={settingAnchorEl}
            sx={{
              width: 'fit-content',
              height: 'fit-content',
              background: colorPalette.monotone[0],
              borderRadius: '4px',
              boxShadow: '0px 1px 2px #0000004D',
              padding: '20px',
              zIndex: 1300,
            }}
            placement={'bottom'}
            modifiers={[{ name: 'offset', options: { offset: [0, 5] } }]}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginRight: '15px',
                  }}
                >
                  <Typography
                    sx={{
                      color: colorPalette.monotone[5],
                      fontSize: '12px',
                      fontWeight: 500,
                      margin: '0 auto 5px 0',
                    }}
                  >
                    {intl.formatMessage({ id: 'wbs.header.gantt.unit' })}
                  </Typography>
                  <ClickAwayListener onClickAway={handleUnitClose}>
                    <div>
                      <GanttSettingPulldown
                        onClick={event => {
                          if (openUnitPopper) {
                            setUnitAnchorEl(undefined)
                            setOpenUnitPopper(false)
                          } else {
                            setUnitAnchorEl(event.currentTarget)
                            setOpenUnitPopper(true)
                          }
                        }}
                        openUnitPopper={openUnitPopper}
                        label={intl.formatMessage({
                          id: `gantt.displayUnit.${unit.toLowerCase()}`,
                        })}
                        disabled={!value ? true : false}
                      />
                      <Popper
                        open={openUnitPopper}
                        anchorEl={unitAnchorEl}
                        sx={{
                          width: '120px',
                          height: 'fit-content',
                          background: colorPalette.monotone[0],
                          borderRadius: '4px',
                          boxShadow: '0px 1px 2px #0000004D',
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '10px 15px',
                          zIndex: 1300,
                        }}
                        placement={'bottom-end'}
                        modifiers={[
                          { name: 'offset', options: { offset: [0, 5] } },
                        ]}
                      >
                        <ConditionButton
                          selected={unit}
                          options={Object.values(GanttDisplayUnit)
                            .filter(
                              v => isGanttV2 || v !== GanttDisplayUnit.QUARTER
                            )
                            .map(v => {
                              return {
                                onClick: () => {
                                  setUnit(v)
                                  if (isGanttV2) {
                                    const newStart =
                                      GanttParameterVO.roundStartDate(
                                        range.start,
                                        v,
                                        startDay
                                      )
                                    const newEnd =
                                      GanttParameterVO.roundEndDate(
                                        range.end,
                                        v,
                                        startDay
                                      )
                                    const newRange = {
                                      start: newStart,
                                      end: newEnd,
                                    }
                                    setRange(newRange)
                                    dispatchParameter(
                                      v,
                                      startDay,
                                      newRange,
                                      displayOptions
                                    )
                                  }
                                },
                                unit: v,
                                label: intl.formatMessage({
                                  id: `gantt.displayUnit.${v.toLowerCase()}`,
                                }),
                              }
                            })}
                        />
                      </Popper>
                    </div>
                  </ClickAwayListener>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: colorPalette.monotone[5],
                      fontSize: '12px',
                      fontWeight: 500,
                      margin: '0 auto 5px 0',
                    }}
                  >
                    {intl.formatMessage({ id: 'wbs.header.gantt.startDay' })}
                  </Typography>
                  <ClickAwayListener onClickAway={handleStartDayClose}>
                    <div>
                      <GanttSettingPulldown
                        onClick={event => {
                          if (openStartDayPopper) {
                            setStartDayAnchorEl(undefined)
                            setOpenStartDayPopper(false)
                          } else {
                            setStartDayAnchorEl(event.currentTarget)
                            setOpenStartDayPopper(true)
                          }
                        }}
                        openUnitPopper={openStartDayPopper}
                        label={intl.formatMessage({
                          id: `dayOfWeek.${startDay.toLowerCase()}`,
                        })}
                        disabled={unit !== GanttDisplayUnit.WEEK || !value}
                      />
                      <Popper
                        open={openStartDayPopper}
                        anchorEl={startDayAnchorEl}
                        sx={{
                          width: '120px',
                          height: 'fit-content',
                          background: colorPalette.monotone[0],
                          borderRadius: '4px',
                          boxShadow: '0px 1px 2px #0000004D',
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '10px 15px',
                          zIndex: 1300,
                        }}
                        placement={'bottom-end'}
                        modifiers={[
                          { name: 'offset', options: { offset: [0, 5] } },
                        ]}
                      >
                        <ConditionButton
                          selected={startDay}
                          options={Object.values(DayOfWeek).map(v => {
                            return {
                              onClick: () => {
                                setStartDay(v)
                                if (isGanttV2) {
                                  dispatchParameter(
                                    unit,
                                    v,
                                    range,
                                    displayOptions
                                  )
                                }
                              },
                              unit: v,
                              label: intl.formatMessage({
                                id: `dayOfWeek.${v.toLowerCase()}`,
                              }),
                            }
                          })}
                        />
                      </Popper>
                    </div>
                  </ClickAwayListener>
                </Box>
              </Box>
              {isGanttV2 ? (
                <>
                  <V2SectionColumn>
                    <V2SectionHeader>
                      <Text variant="2sMedium" color={colorPalette.monotone[5]}>
                        {intl.formatMessage({
                          id: 'wbs.header.gantt.displayRange',
                        })}
                      </Text>
                      <V2PresetSelectContainer>
                        <Select
                          fullWidth={true}
                          value={rangePreset}
                          IconComponent={ExpandMore}
                          open={isPresetSelectOpen}
                          onOpen={() => setIsPresetSelectOpen(true)}
                          onClose={() => setIsPresetSelectOpen(false)}
                          MenuProps={{
                            sx: { zIndex: 1400 },
                          }}
                          onChange={(e: SelectChangeEvent<unknown>) => {
                            const newPreset = e.target.value as RangePreset
                            if (newPreset === RANGE_PRESET_FIT_TO_ROOT) {
                              const fitStartDate =
                                rootScheduledStartDate ??
                                projectScheduledStartDate
                              const fitEndDate =
                                rootScheduledEndDate ?? projectScheduledEndDate
                              if (!fitStartDate || !fitEndDate) return
                              const newStart = GanttParameterVO.roundStartDate(
                                new DateVO(fitStartDate),
                                unit,
                                startDay
                              )
                              const newEnd = GanttParameterVO.roundEndDate(
                                new DateVO(fitEndDate),
                                unit,
                                startDay
                              )
                              const clamped = GanttParameterVO.clampRange(
                                newStart,
                                newEnd,
                                'start'
                              )
                              setRange(clamped)
                              dispatchParameter(
                                unit,
                                startDay,
                                clamped,
                                displayOptions
                              )
                            }
                            setRangePreset(newPreset)
                          }}
                          disableUnderline={true}
                          options={[
                            {
                              value: RANGE_PRESET_FIT_TO_ROOT,
                              label: intl.formatMessage({
                                id: 'gantt.fitToRootPeriod',
                              }),
                              disabled:
                                !(
                                  rootScheduledStartDate ??
                                  projectScheduledStartDate
                                ) ||
                                !(
                                  rootScheduledEndDate ??
                                  projectScheduledEndDate
                                ),
                            },
                            {
                              value: RANGE_PRESET_DATE_SPECIFIED,
                              label: intl.formatMessage({
                                id: 'dateSpecified',
                              }),
                            },
                          ]}
                          sx={{
                            fontSize: '12px',
                            color: colorPalette.monotone[4],
                          }}
                          disabled={!value}
                        />
                      </V2PresetSelectContainer>
                    </V2SectionHeader>
                    <V2DateRangeRow>
                      <V2ShiftButton
                        onClick={() => shiftBound('start', -1)}
                        disabled={!value || isAtMaxRange}
                        size="small"
                      >
                        <ArrowBackIos sx={{ fontSize: '12px' }} />
                      </V2ShiftButton>
                      <DateSelect
                        type={dateSelectType}
                        value={range.start}
                        onChange={(value?: string) => {
                          if (!value) return
                          const rounded = GanttParameterVO.roundStartDate(
                            new DateVO(value),
                            unit,
                            startDay
                          )
                          const newEnd = range.end.isBefore(rounded)
                            ? GanttParameterVO.roundEndDate(
                                rounded,
                                unit,
                                startDay
                              )
                            : range.end
                          const clamped = GanttParameterVO.clampRange(
                            rounded,
                            newEnd,
                            'start'
                          )
                          setRange(clamped)
                          setRangePreset(RANGE_PRESET_DATE_SPECIFIED)
                          debouncedDispatchRange(clamped)
                        }}
                        disabled={!value}
                        sx={{ width: '120px' }}
                        calendarStartDay={calendarStartDay}
                      />
                      <V2ShiftButton
                        onClick={() => shiftBound('start', 1)}
                        disabled={!value}
                        size="small"
                      >
                        <ArrowForwardIos sx={{ fontSize: '12px' }} />
                      </V2ShiftButton>
                      <V2DateSeparator
                        variant="2s"
                        color={colorPalette.monotone[3]}
                      >
                        ～
                      </V2DateSeparator>
                      <V2ShiftButton
                        onClick={() => shiftBound('end', -1)}
                        disabled={!value}
                        size="small"
                      >
                        <ArrowBackIos sx={{ fontSize: '12px' }} />
                      </V2ShiftButton>
                      <DateSelect
                        type={dateSelectType}
                        value={range.end}
                        onChange={(value?: string) => {
                          if (!value) return
                          const rounded = GanttParameterVO.roundEndDate(
                            new DateVO(value),
                            unit,
                            startDay
                          )
                          const newStart = range.start.isAfter(rounded)
                            ? GanttParameterVO.roundStartDate(
                                rounded,
                                unit,
                                startDay
                              )
                            : range.start
                          const clamped = GanttParameterVO.clampRange(
                            newStart,
                            rounded,
                            'end'
                          )
                          setRange(clamped)
                          setRangePreset(RANGE_PRESET_DATE_SPECIFIED)
                          debouncedDispatchRange(clamped)
                        }}
                        disabled={!value}
                        sx={{ width: '120px' }}
                        calendarStartDay={calendarStartDay}
                      />
                      <V2ShiftButton
                        onClick={() => shiftBound('end', 1)}
                        disabled={!value || isAtMaxRange}
                        size="small"
                      >
                        <ArrowForwardIos sx={{ fontSize: '12px' }} />
                      </V2ShiftButton>
                    </V2DateRangeRow>
                  </V2SectionColumn>
                  <V2SectionColumn>
                    <Text
                      variant="2sMedium"
                      color={colorPalette.monotone[5]}
                      style={{ marginBottom: '5px' }}
                    >
                      {intl.formatMessage({
                        id: 'gantt.displayOption.title',
                      })}
                    </Text>
                    <V2OptionsGrid row={true}>
                      {(
                        [
                          'showTypeIcon',
                          'showStatus',
                          'showName',
                          'showAssignee',
                          'showProgressRate',
                        ] as (keyof GanttDisplayOptions)[]
                      ).map(key => (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              size="s"
                              checked={displayOptions[key]}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const newOptions = {
                                  ...displayOptions,
                                  [key]: e.target.checked,
                                }
                                setDisplayOptions(newOptions)
                                dispatchParameter(
                                  unit,
                                  startDay,
                                  range,
                                  newOptions
                                )
                              }}
                            />
                          }
                          label={intl.formatMessage({
                            id: `gantt.displayOption.${key}`,
                          })}
                          sx={{
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              color: colorPalette.monotone[5],
                              fontSize: '12px',
                            },
                          }}
                          disabled={!value}
                        />
                      ))}
                    </V2OptionsGrid>
                  </V2SectionColumn>
                  <ProgressLineContainer>
                    <Text variant="2s" color={colorPalette.monotone[5]}>
                      {intl.formatMessage({
                        id: 'gantt.progressLine',
                      })}
                    </Text>
                    <Switch
                      size="small"
                      checked={displayOptions.showProgressLine}
                      onChange={() => {
                        const newOptions = {
                          ...displayOptions,
                          showProgressLine: !displayOptions.showProgressLine,
                        }
                        setDisplayOptions(newOptions)
                        dispatchParameter(unit, startDay, range, newOptions)
                      }}
                      disabled={!value}
                    />
                  </ProgressLineContainer>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '20px',
                    width: 'max-content',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      color: colorPalette.monotone[5],
                      fontSize: '12px',
                      fontWeight: 500,
                      margin: '0 auto 5px 0',
                    }}
                  >
                    {intl.formatMessage({
                      id: 'wbs.header.gantt.displayRange',
                    })}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        marginRight: '10px',
                        width: '120px',
                        heigth: '30px',
                      }}
                    >
                      <DateSelect
                        type={DateSelectType.DATE}
                        value={range.start}
                        onChange={(value?: string) => {
                          if (!value) return
                          const date = new DateVO(value)
                          setRange({
                            start: date,
                            end: range.end.isBefore(date) ? date : range.end,
                          })
                        }}
                        sx={{ marginRight: '10px' }}
                        disabled={!value ? true : false}
                      />
                    </Box>
                    <Box
                      sx={{
                        marginRight: '10px',
                        width: '120px',
                        heigth: '30px',
                      }}
                    >
                      <DateSelect
                        type={DateSelectType.DATE}
                        value={range.end}
                        onChange={(value?: string) => {
                          if (!value) return
                          const date = new DateVO(value)
                          setRange({
                            start: range.start.isAfter(date)
                              ? date
                              : range.start,
                            end: date,
                          })
                        }}
                        disabled={!value ? true : false}
                      />
                    </Box>
                    <Button
                      sx={{ marginLeft: '5px' }}
                      onClick={handleGanttChartSettingApply}
                      disabled={!value ? true : false}
                    >
                      <Typography>
                        {intl.formatMessage({ id: 'wbs.header.gantt.apply' })}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Popper>
        </div>
      </ClickAwayListener>
    </Box>
  )
}

type ConditionProps = {
  selected: GanttDisplayUnit | DayOfWeek
  options?: {
    onClick: () => void
    unit: GanttDisplayUnit | DayOfWeek
    label: string
  }[]
}

const ConditionButton = ({ selected, options }: ConditionProps) => {
  return (
    <div>
      {options &&
        options.map((option, i) => (
          <MuiButton
            key={i}
            onClick={option.onClick}
            sx={{
              background: () =>
                selected === option.unit
                  ? colorPalette.skyBlue[0]
                  : 'transparent',
              justifyContent: 'start',
              color: colorPalette.monotone[4],
              '&:hover': {
                background: () =>
                  selected === option.unit
                    ? colorPalette.skyBlue[0]
                    : 'transparent',
              },
            }}
          >
            <Typography>{option.label}</Typography>
          </MuiButton>
        ))}
    </div>
  )
}

type PulldownProps = {
  onClick: MouseEventHandler<HTMLButtonElement> | undefined
  openUnitPopper: boolean
  label?: string
  disabled?: boolean
}
const GanttSettingPulldown = ({
  onClick,
  openUnitPopper,
  label,
  disabled,
}: PulldownProps) => {
  return (
    <MuiButton
      sx={{
        border: `1px solid ${colorPalette.monotone[2]}`,
        background: colorPalette.monotone[0],
        '&:hover': { background: colorPalette.monotone[0] },
        color: colorPalette.monotone[4],
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: isGanttV2 ? '120px' : '160px',
      }}
      onClick={onClick}
      disabled={disabled}
    >
      <Typography
        sx={{
          margin: '0 auto 0 10px',
          fontSize: 12,
        }}
      >
        {label}
      </Typography>
      {openUnitPopper ? (
        <ExpandLess
          sx={{
            margin: '0 5px 0 auto',
            height: '13px',
            width: '13px',
          }}
        />
      ) : (
        <ExpandMore
          sx={{
            margin: '0 5px 0 auto',
            height: '13px',
            width: '13px',
          }}
        />
      )}
    </MuiButton>
  )
}

const mapStateToProps = (state: AllState) => ({
  projectUuid: state.project.selected,
  parameter: state.project.ganttParameter,
  projectScheduledStartDate: state.project.current?.scheduledDate?.startDate,
  projectScheduledEndDate: state.project.current?.scheduledDate?.endDate,
})

export default connect(mapStateToProps)(GanttChartParameter)
